import { jsx } from 'react/jsx-runtime';
import { createContext, useState, useEffect, useContext } from 'react';
import { useTranslation } from '../hooks/useTranslations.js';
import { selectCreditCard, storeCreditCard, deleteCreditCard, deleteAllCreditCards } from '../services/creditCardService.js';
import { selectCardAndUnselectPrevious, deleteCardArrayAndUpdate } from '../utils/cardUtils.js';
import { useAppContext } from './AppContextProvider.js';
import { useConfig } from './ConfigProvider.js';
import { useOverlays, OverlayIds } from './OverlaysProvider.js';

const CreditCardsMutationContext = createContext({
    initiateCardDeletion: () => null,
    saveCreditCard: () => Promise.resolve(false),
    saveAdyenCreditCard: () => Promise.resolve(false),
    deleteCard: () => Promise.resolve(),
    deleteAllCards: () => Promise.resolve(false),
    makeCreditCardActive: () => Promise.resolve(false),
    data: { creditCards: [] },
});
/**
 * Manages the list of credit cards of the customer, by proposing to the other components the methods to select, delete and add credit cards.
 * Each method will call the appropriate method from the creditCardService to send a request to the backend.
 */
const CreditCardsMutationProvider = ({ children, pspToken, psp, creditCards: inputCards, }) => {
    const { t } = useTranslation();
    const { config } = useConfig();
    const { csrfToken } = useAppContext();
    const { showOverlay } = useOverlays();
    const [creditCards, setCreditCards] = useState(inputCards);
    const [cardToDelete, setCartToDelete] = useState();
    const initiateCardDeletion = (card) => {
        setCartToDelete(card);
        showOverlay(OverlayIds.DELETE_CARD);
    };
    const makeCreditCardActive = async (creditCard) => {
        // Optimistic UI: we display the card as selected before actually making the request
        const previousSelectedCard = creditCards.find((c) => c.selected);
        setCreditCards((currentCreditCards) => selectCardAndUnselectPrevious(currentCreditCards, creditCard.pspIdentifier));
        function revertOptimisticUI() {
            setCreditCards((currentCreditCards) => selectCardAndUnselectPrevious(currentCreditCards, previousSelectedCard ? previousSelectedCard.pspIdentifier : null));
        }
        try {
            const isSuccess = await selectCreditCard({
                csrfToken: csrfToken,
                creditCardIdentifier: creditCard.pspIdentifier,
                psp,
            });
            if (!isSuccess) {
                revertOptimisticUI();
            }
            return isSuccess;
        }
        catch (error) {
            // just catch the error in passing, to ensure to revert the UI since the card could not be selected
            revertOptimisticUI();
            throw error;
        }
    };
    /**
     * Store the Braintree Credit Card data in a checkout session.
     *
     * @param tokenizationResult The Braintree Card tokenization result.
     * @return The boolean value indicate if this operation is successful.
     */
    const saveCreditCard = async (tokenizationResult) => {
        if (tokenizationResult.success) {
            try {
                const { data } = tokenizationResult;
                const body = {
                    braintreeNonce: data.nonce,
                    braintreeLastFourNumber: data.details.lastFour,
                    braintreeCardholderName: data.details.cardholderName,
                    braintreeExpirationMonth: data.details.expirationMonth,
                    braintreeExpirationYear: data.details.expirationYear,
                    braintreeCardType: data.details.cardType,
                    braintreeStoreCard: data.storeInVault,
                    braintreeBinData: data.binData,
                    braintreeBin: data.details.bin,
                };
                const params = {
                    csrfToken: csrfToken,
                    psp: 'BRAINTREE',
                };
                await storeCreditCard(params, body);
                if (config.env !== 'local') {
                    window.location.assign('/checkout/preview');
                }
                return true;
            }
            catch (error) {
                throw new Error(t('checkout.payment.creditcard.error.addcard.apicall'));
            }
        }
        else if (tokenizationResult.errorDisplayable) {
            throw new Error(t('checkout.payment.creditcard.error.addcard.apicall'));
        }
        else {
            return false;
        }
    };
    /**
     * Store the Adyen Credit Card data in a checkout session.
     */
    const saveAdyenCreditCard = (data) => {
        const params = {
            csrfToken: csrfToken,
            psp: 'ADYEN',
        };
        return storeCreditCard(params, data);
    };
    const deleteCard = async () => {
        if (!cardToDelete) {
            return;
        }
        const newSelectedCardPspIdentifier = await deleteCreditCard({
            csrfToken: csrfToken,
            creditCardIdentifier: cardToDelete.pspIdentifier,
            psp,
        });
        // Remove deleted and update selection if changed
        const updatedCards = deleteCardArrayAndUpdate(creditCards, cardToDelete, newSelectedCardPspIdentifier);
        setCreditCards(updatedCards);
    };
    const deleteAllCards = async () => {
        const isSuccess = await deleteAllCreditCards({ csrfToken: csrfToken, psp });
        // if (isSuccess) {
        setCreditCards([]);
        // }
        return isSuccess;
    };
    useEffect(() => {
        setCreditCards(inputCards);
    }, [inputCards]);
    return (jsx(CreditCardsMutationContext.Provider, { value: {
            initiateCardDeletion,
            deleteCard,
            deleteAllCards,
            makeCreditCardActive,
            saveCreditCard,
            saveAdyenCreditCard,
            data: {
                btToken: pspToken,
                cardToDelete,
                creditCards,
            },
        }, children: children }));
};
const useCreditcardService = () => {
    return useContext(CreditCardsMutationContext);
};

export { CreditCardsMutationProvider as default, useCreditcardService };
//# sourceMappingURL=CreditCardsMutationProvider.js.map
